import { RefreshCw } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  isApiError,
  type ProjectSummary,
  type RailwayResyncEnvironment,
  useAdminResyncProjectRailway,
} from "@/lib/api";
import { logger } from "@/lib/logger";

interface ProjectRailwayResyncItemProps {
  project: ProjectSummary;
}

const MAY_NOT_HAVE_FINISHED =
  "The resync may not have finished. Check the project's Railway variables before you run it again.";

/** What to tell the admin when the resync request fails. */
function resyncErrorMessage(err: unknown): string {
  // No answer (status 0: the connection dropped, and the client puts the
  // browser's own "Failed to fetch" in `detail`): the server may still be
  // writing, or may already have replaced the session secret.
  if (!isApiError(err) || err.status === 0) {
    return MAY_NOT_HAVE_FINISHED;
  }
  // The backend's own refusals (4xx) and the 5xx it sends before any write.
  const detail = (err.body as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (err.status >= 400 && err.status < 500) {
    return "The resync could not start. Please try again.";
  }
  // A gateway timeout or another answer without a detail: it may still be writing.
  return MAY_NOT_HAVE_FINISHED;
}

/**
 * Admin action (BOIE-271 / BOIE-258): bring a project's Railway services to the
 * template's runtime contract. A menu item that opens its own confirmation
 * dialog, which then shows what changed in each environment. While a resync
 * runs the dialog stays open, so its outcome (a replaced session secret
 * included) is never lost.
 */
export function ProjectRailwayResyncItem({ project }: ProjectRailwayResyncItemProps) {
  const resync = useAdminResyncProjectRailway();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const handleResync = async () => {
    setError("");
    try {
      await resync.mutateAsync({ projectId: project.id });
    } catch (err) {
      logger.error("[admin-projects] Railway resync failed:", err);
      setError(resyncErrorMessage(err));
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && resync.isPending) {
      logger.debug("[admin-projects] resync dialog kept open while the resync runs");
      return;
    }
    if (!next) {
      resync.reset();
      setError("");
    }
    setOpen(next);
  };

  const environments = resync.data?.environments ?? [];

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <RefreshCw className="mr-2 size-4" /> Resync Railway settings
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resync Railway settings for {project.project_name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This brings the project&apos;s Railway services, and its prod ones once promoted, in
            line with the current template: each side redeploys only when its own code changes,
            session cookies are secure, and sign-in settings live on the backend only. If the
            app&apos;s session secret is the one in the downloadable <code>.env</code> (true for
            projects created before this fix), it is replaced, which{" "}
            <strong>signs everyone out of the app once</strong>. The services redeploy afterwards.
            Each environment is first checked against the code it deploys: one still on a template
            before v1.6.4, or one whose template ScaffoldUI cannot confirm on GitHub, is left
            untouched, and the result says why.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {environments.length > 0 && (
          <div className="space-y-3 text-sm">
            {environments.map((env) => (
              <ResyncResult key={env.environment} env={env} />
            ))}
          </div>
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={resync.isPending}>
            {environments.length > 0 ? "Close" : "Cancel"}
          </AlertDialogCancel>
          {environments.length === 0 && (
            <Button onClick={() => void handleResync()} disabled={resync.isPending}>
              {resync.isPending ? "Resyncing..." : "Resync"}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ResyncResult({ env }: { env: RailwayResyncEnvironment }) {
  const set = env.backend_variables_set ?? [];
  const removed = [
    ...(env.backend_variables_removed ?? []).map((name) => `${name} (backend)`),
    ...(env.frontend_variables_removed ?? []).map((name) => `${name} (frontend)`),
  ];
  const warnings = env.warnings ?? [];
  return (
    <div className="border-border rounded-md border p-3">
      <p className="text-foreground font-medium capitalize">{env.environment}</p>
      {env.error ? (
        <p className="text-destructive mt-1">Failed: {env.error}</p>
      ) : (
        <p className="text-muted mt-1">
          {env.watch_paths_set ? "Watch paths set." : "Watch paths not set."}{" "}
          {set.length > 0 ? `Set on the backend: ${set.join(", ")}.` : "No variable to set."}{" "}
          {removed.length > 0 ? `Removed: ${removed.join(", ")}.` : "Nothing to remove."}
        </p>
      )}
      {env.error && (set.length > 0 || removed.length > 0) && (
        <p className="text-muted mt-1">
          Before it failed: {[...set.map((name) => `set ${name}`), ...removed].join(", ")}.
        </p>
      )}
      {env.auth_secret_rotated && (
        <p className="mt-1 text-amber-700 dark:text-amber-400">
          The session secret was replaced: everyone was signed out once.
        </p>
      )}
      {warnings.map((warning) => (
        <p key={warning} className="mt-1 text-amber-700 dark:text-amber-400">
          {warning}
        </p>
      ))}
    </div>
  );
}
